import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Eye, EyeOff } from "lucide-react";

// Context broadcasts a "global command" (version + desired visibility).
// Each HiddenMoney listens to the command. After the command, the user can
// still toggle each instance individually via its own eye icon.
const GlobalVisibilityCtx = createContext({
  command: { version: 0, visible: false },
  showAll: () => {},
  hideAll: () => {},
});

export function PriceVisibilityProvider({ children }) {
  const [command, setCommand] = useState({ version: 0, visible: false });

  const showAll = useCallback(() => {
    setCommand((c) => ({ version: c.version + 1, visible: true }));
  }, []);
  const hideAll = useCallback(() => {
    setCommand((c) => ({ version: c.version + 1, visible: false }));
  }, []);

  return (
    <GlobalVisibilityCtx.Provider value={{ command, showAll, hideAll }}>
      {children}
    </GlobalVisibilityCtx.Provider>
  );
}

export function useGlobalVisibility() {
  return useContext(GlobalVisibilityCtx);
}

// Each instance is independent — its own visibility state + own toggle icon.
// Default = hidden. Reacts to global show-all/hide-all commands while still
// allowing individual toggling afterwards.
export function HiddenMoney({ value, className = "", testid, iconSize = "sm" }) {
  const { command } = useContext(GlobalVisibilityCtx);
  const [visible, setVisible] = useState(false);

  // Apply global command whenever its version changes.
  useEffect(() => {
    if (command.version > 0) setVisible(command.visible);
  }, [command.version, command.visible]);

  const iconCls = iconSize === "lg" ? "h-4 w-4" : "h-3.5 w-3.5";
  const btnCls = iconSize === "lg" ? "h-7 w-7" : "h-6 w-6";

  return (
    <span className="inline-flex items-center gap-1.5 align-middle">
      <span className={className} data-testid={testid}>
        {visible ? value : <span className="select-none tracking-[0.2em] opacity-80">••••••</span>}
      </span>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setVisible((v) => !v); }}
        className={`${btnCls} inline-flex items-center justify-center rounded-md text-zinc-400 hover:text-teal-700 hover:bg-teal-50 transition-colors no-print`}
        title={visible ? "إخفاء" : "إظهار"}
        aria-label={visible ? "إخفاء المبلغ" : "إظهار المبلغ"}
      >
        {visible ? <Eye className={iconCls} /> : <EyeOff className={iconCls} />}
      </button>
    </span>
  );
}

// Backward-compat hook (some old code may import it)
export const usePriceVisibility = () => ({ visible: false, toggle: () => {}, set: () => {} });
